/**
 * JWT Exchange Hook
 *
 * Handles the final step of onboarding: exchanging the JWT token
 * for an authenticated session cookie.
 *
 * IMPORTANT: This calls auth, not user-portal-api
 */

import { useCallback } from 'react';
import * as api from '@/features/onboarding/api/onboarding-api';
import {
  FlowStatus,
  type OnboardingState,
} from '@/features/onboarding/types/onboarding.types';

interface UseJwtExchangeProps {
  state: OnboardingState;
  setState: React.Dispatch<React.SetStateAction<OnboardingState>>;
}

interface UseJwtExchangeReturn {
  exchangeForSession: () => Promise<boolean>;
  canExchange: boolean;
}

/**
 * Hook for JWT exchange actions
 */
export function useJwtExchange({
  state,
  setState,
}: UseJwtExchangeProps): UseJwtExchangeReturn {
  /**
   * Exchange JWT token for authenticated session cookie
   * Called automatically when flow completes with exchangeToken
   *
   * IMPORTANT: This calls auth, not user-portal-api
   * The exchange sets an HttpOnly cookie for authentication.
   */
  const exchangeForSession = useCallback(async () => {
    if (!state.exchangeToken) {
      setState(prev => ({
        ...prev,
        exchangeError: 'No exchange token available',
      }));

      return false;
    }

    setState(prev => ({
      ...prev,
      isExchanging: true,
      exchangeError: null,
    }));

    try {
      const response = await api.exchangeJwtForSession(state.exchangeToken);

      if (response.success) {
        setState(prev => ({
          ...prev,
          isExchanging: false,
          isAuthenticated: true,
          sessionTtl: response.authenticatedSessionTtl,
        }));

        return true;
      } else {
        throw new Error('Exchange failed');
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        exchangeError:
          error instanceof Error ? error.message : 'Authentication failed',
        isExchanging: false,
      }));

      return false;
    }
  }, [state.exchangeToken, setState]);

  // Can exchange when flow is complete and we have a token
  const canExchange =
    state.flowStatus === FlowStatus.COMPLETED && !!state.exchangeToken;

  return {
    exchangeForSession,
    canExchange,
  };
}
